import { get, includes, isEmpty, isFunction, noop, omit } from 'lodash'
import React, { FocusEvent, useEffect, useState } from 'react'
import { Form, FormControlProps, InputGroup } from 'react-bootstrap'
import styled from 'styled-components'

import Icon from '../Icon'
import Spinner from '../Spinner'
import { useInputContext } from './InputContext'
export { default as useInput } from './useInput'
export { InputContextProvider } from './InputContext'

import { Tooltip } from '@mantine/core'

import { usePrevious } from '../../hooks'
import { propsToSize, TPropsToSize } from '../bootstrapHelper'
import PrefixIcon from './PrefixIcon'

const Wrapper = styled.div`
  position: relative;
`

const SpinnerWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: calc(25%);
  display: flex;

  & > * {
    margin-left: 10px;
  }
`

export interface TInput
  extends Omit<FormControlProps, 'onChange'>,
    TPropsToSize {
  isLoading?: boolean
  /** Ref of the <input> */
  innerRef?: React.ForwardedRef<unknown>
  /** Callback when press enter */
  onEnter?: () => void
  onClear?: () => void
  onFocus?: (e?: React.FocusEvent<HTMLInputElement>) => void
  onKeyPress?: (e?: React.KeyboardEvent<HTMLInputElement>) => void
  onKeyUp?: (e?: React.KeyboardEvent<HTMLInputElement>) => void
  onPaste?: (e?: React.ClipboardEvent<HTMLInputElement>) => void
  append?: React.ReactNode
  appendButton?: React.ReactNode
  prepend?: React.ReactNode
  /** JSX Style object. Mainly used for setting height on textareas */
  style?: React.CSSProperties
  /** Placeholder */
  placeholder?: string
  wrapperStyle?: React.CSSProperties
  preventValues?: string[]
  disabled?: boolean
  autofocus?: boolean
  autoselect?: boolean
  rawEvent?: boolean
  name?: string
  label?: string
  onChange?: ((val: any) => void) | React.ChangeEventHandler<HTMLInputElement>
  accept?: string
  default?: string
  onClick?: (e?: React.MouseEvent<HTMLDivElement>) => void
  autoFocus?: boolean
  changeOnBlur?: boolean
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  isClearable?: boolean
  showTextInTooltip?: boolean
  spinnerSize?: number
  icon?: string
}

const Input = (props: TInput): React.ReactElement => {
  const {
    isLoading,
    innerRef,
    onEnter = noop,
    value,
    onChange = noop,
    onClear,
    isClearable,
    as,
    append,
    appendButton,
    prepend,
    style,
    placeholder,
    wrapperStyle = {},
    preventValues = [],
    disabled = false,
    autofocus,
    autoFocus,
    autoselect,
    onPaste,
    rawEvent,
    changeOnBlur,
    showTextInTooltip,
    spinnerSize = 18,
    icon,
    ...rest
  } = props

  const { onEnter: onContextEnter } = useInputContext()

  const [localValue, setLocalValue] = useState(value)

  const previousValue = usePrevious(value)
  useEffect(() => {
    if (previousValue !== value) {
      setLocalValue(value)
    }
  }, [previousValue, value])

  const onChangeLocal: React.ChangeEventHandler<HTMLInputElement> = (v) => {
    const inputValue =
      (get(v, 'nativeEvent', v) as any) instanceof Event
        ? (get(v, 'target.value') as any)
        : v

    if (!includes(preventValues, inputValue)) {
      changeOnBlur ? setLocalValue(inputValue) : onChange(inputValue)
    }
  }

  const [isFocused, setIsFocused] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const onFocus = (e: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true)
  }

  const onBlur = (e: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false)
    if (changeOnBlur) {
      onChange(localValue)
    }
    if (isFunction(props.onBlur)) {
      props.onBlur(e)
    }
  }

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      if (isFunction(onEnter)) {
        onEnter()
      }
      if (isFunction(onContextEnter)) {
        onContextEnter()
      }
    }
    if (isFunction(props.onKeyPress)) {
      props.onKeyPress(e)
    }
  }

  const clearValue = (): void => {
    if (onClear) {
      onClear()
    } else {
      onChange('')
    }
  }

  const formControlProps: any = omit(rest, [
    'parse',
    'format',
    'validate',
    'parentFieldProps',
  ])

  const inputStyle = icon ? { paddingLeft: '32px' } : undefined

  const inner = (
    <Tooltip
      hidden={!showTextInTooltip || isEmpty(localValue)}
      label={localValue}
      opened={showTextInTooltip && (isFocused || isHovered)}
      withArrow
    >
      <Wrapper style={{ ...wrapperStyle }} {...formControlProps}>
        <Form.Control
          {...formControlProps}
          ref={innerRef}
          value={changeOnBlur ? localValue : value}
          onChange={rawEvent ? props.onChange : onChangeLocal}
          onKeyPress={onKeyPress}
          autoFocus={autofocus || autoFocus}
          onPaste={onPaste}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            onFocus(e)
            if (autofocus || autoselect) {
              try {
                e.currentTarget.select()
              } catch (err) {
                return
              }
            }
            if (props.onFocus) {
              props.onFocus(e)
            }
          }}
          onBlur={onBlur || props.onBlur}
          cy-name={props.name}
          as={as}
          placeholder={placeholder}
          style={{ ...style, ...inputStyle }}
          disabled={disabled}
          size={propsToSize(props) as 'sm' | 'lg'}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
        {icon && <PrefixIcon icon={icon} />}
        <SpinnerWrapper>
          {isLoading && <Spinner size={spinnerSize} />}
          {(isClearable || onClear) && value && value !== '' && (
            <div style={{ marginTop: '2px' }}>
              <Icon
                icon="fas fa-times-circle"
                onClick={clearValue}
                tooltip={i18n.t('clear_content', 'Inhalt leeren')}
              />
            </div>
          )}
        </SpinnerWrapper>
      </Wrapper>
    </Tooltip>
  )

  if (append || prepend || appendButton) {
    return (
      <InputGroup className="d-flex" style={{ flexWrap: 'nowrap' }}>
        {prepend && (
          <InputGroup.Prepend>
            <InputGroup.Text>{prepend}</InputGroup.Text>
          </InputGroup.Prepend>
        )}
        <div className="flex-fill">{inner}</div>
        {append && (
          <InputGroup.Append>
            <InputGroup.Text>{append}</InputGroup.Text>
          </InputGroup.Append>
        )}
        {appendButton && <InputGroup.Append>{appendButton}</InputGroup.Append>}
      </InputGroup>
    )
  }

  return inner
}

export default Input

export const RawInput = React.forwardRef((props, ref) => (
  <Input {...props} innerRef={ref} rawEvent />
))
